import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, ArrowCounterClockwise, Bank, CreditCard, Clock, SealCheck, Money, Warning, ClockCounterClockwise, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [leases, setLeases] = useState([]);
  const [fStatus, setFStatus] = useState("all");
  const [fLease, setFLease] = useState("all");
  const [payDialog, setPayDialog] = useState(null); // payment being settled
  const [payForm, setPayForm] = useState({ paid_date: new Date().toISOString().slice(0, 10), reference: "" });
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const nav = useNavigate();

  const load = useCallback(() => {
    const params = {};
    if (fStatus !== "all") params.status = fStatus;
    if (fLease !== "all") params.lease_id = fLease;
    api.get("/payments", { params }).then((r) => setPayments(r.data));
  }, [fStatus, fLease]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/leases").then((r) => setLeases(r.data)); }, []);

  const approve = async (p) => {
    try { await api.post(`/payments/${p.lease_id}/${p.period}/approve`); toast.success("Payment approved"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const reset = async (p) => {
    try { await api.post(`/payments/${p.lease_id}/${p.period}/reset`); toast.success("Approval reverted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openPay = (p) => { setPayForm({ paid_date: new Date().toISOString().slice(0, 10), reference: "" }); setPayDialog(p); };
  const submitPay = async () => {
    if (!payForm.paid_date) { toast.error("Payment date is required"); return; }
    try {
      await api.post(`/payments/${payDialog.lease_id}/${payDialog.period}/pay`, payForm);
      toast.success("Payment marked as paid");
      setPayDialog(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const unpay = async (p) => {
    try { await api.post(`/payments/${p.lease_id}/${p.period}/unpay`); toast.success("Reverted to approved"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const pendingList = payments.filter((p) => p.status === "created");
  const pending = pendingList.length;
  const totalsByCur = {};
  pendingList.forEach((p) => { totalsByCur[p.currency] = (totalsByCur[p.currency] || 0) + p.amount; });
  const totalsStr = Object.entries(totalsByCur).map(([cur, v]) => money(v, cur)).join(" · ") || money(0);
  const overdueCount = payments.filter((p) => p.overdue).length;
  const displayed = onlyOverdue ? payments.filter((p) => p.overdue) : payments;

  const exportReceipts = async () => {
    try {
      const res = await api.get("/payments/receipts.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = "Paid-Receipts.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Receipts exported");
    } catch { toast.error("Could not export receipts"); }
  };

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-2">Schedule & Approvals</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">Payments</h1>
        <p className="text-xs text-muted-foreground mt-2">
          Consolidated payment schedule across all leases by due date. Each payment starts as <span className="font-mono">created</span> and moves to <span className="font-mono">approved</span> after a one-step approval.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 fade-up">
        <div className="w-48">
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="rounded-sm" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="created">Created (pending)</SelectItem>
              <SelectItem value="first_approved">Awaiting 2nd approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-64">
          <Select value={fLease} onValueChange={setFLease}>
            <SelectTrigger className="rounded-sm" data-testid="filter-lease"><SelectValue placeholder="All leases" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All leases</SelectItem>
              {leases.map((l) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          <Button variant={onlyOverdue ? "default" : "outline"} size="sm" onClick={() => setOnlyOverdue((v) => !v)} data-testid="overdue-toggle"
            className={`rounded-sm gap-1.5 ${onlyOverdue ? "" : "text-destructive border-destructive/40"}`}>
            <Warning size={15} /> Overdue ({overdueCount})
          </Button>
          <Button variant="outline" size="sm" onClick={exportReceipts} data-testid="export-receipts-btn" className="rounded-sm gap-1.5">
            <DownloadSimple size={15} /> Export Receipts
          </Button>
          <div className="text-sm text-muted-foreground">
            <span className="font-mono font-semibold text-foreground" data-testid="pending-count">{pending}</span> pending · <span className="font-mono font-semibold text-foreground">{totalsStr}</span>
          </div>
        </div>
      </div>

      {payments.length === 0 ? (
        <Card className="rounded-sm border-dashed border-border shadow-none p-16 text-center bg-card fade-up">
          <CreditCard size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-sm">No payments scheduled. Create a lease to generate its payment schedule.</p>
        </Card>
      ) : (
        <Card className="rounded-sm border-border shadow-none overflow-hidden bg-card fade-up">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Due Date</TableHead>
                  <TableHead>Lease</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Bank</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayed.map((p) => (
                  <TableRow key={p.key} data-testid={`payment-row-${p.key}`} className={p.overdue ? "border-l-2 border-l-destructive" : ""}>
                    <TableCell className={`font-mono text-xs ${p.overdue ? "text-destructive font-semibold" : ""}`}>
                      {p.due_date}
                      {p.overdue && <span className="ml-1 inline-flex items-center" title="Overdue"><Warning size={12} className="text-destructive inline" /></span>}
                    </TableCell>
                    <TableCell>
                      <button className="text-sm font-medium hover:text-primary transition-colors" onClick={() => nav(`/leases/${p.lease_id}`)}>{p.lease_title}</button>
                      <div className="text-xs text-muted-foreground">Period {p.period}</div>
                    </TableCell>
                    <TableCell className="text-sm">{p.supplier?.name || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>
                      {p.supplier && (p.supplier.bank_name || p.supplier.iban || p.supplier.account_number) ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 rounded-sm gap-1 text-xs" data-testid={`bank-${p.key}`}><Bank size={14} /> View</Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 bg-card">
                            <div className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2">Payment Bank Details</div>
                            <div className="space-y-1 text-sm">
                              {p.supplier.bank_name && <div className="flex justify-between"><span className="text-muted-foreground">Bank</span><span className="font-mono">{p.supplier.bank_name}</span></div>}
                              {p.supplier.account_name && <div className="flex justify-between"><span className="text-muted-foreground">Account</span><span className="font-mono">{p.supplier.account_name}</span></div>}
                              {p.supplier.account_number && <div className="flex justify-between"><span className="text-muted-foreground">A/C No.</span><span className="font-mono">{p.supplier.account_number}</span></div>}
                              {p.supplier.iban && <div className="flex justify-between"><span className="text-muted-foreground">IBAN</span><span className="font-mono">{p.supplier.iban}</span></div>}
                              {p.supplier.swift && <div className="flex justify-between"><span className="text-muted-foreground">SWIFT</span><span className="font-mono">{p.supplier.swift}</span></div>}
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono">{money(p.amount, p.currency)}</TableCell>
                    <TableCell>
                      {p.status === "paid" ? (
                        <Badge className="rounded-sm gap-1 bg-emerald-700 hover:bg-emerald-700"><Money size={12} /> Paid</Badge>
                      ) : p.status === "approved" ? (
                        <Badge className="rounded-sm gap-1 bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]"><CheckCircle size={12} /> Approved</Badge>
                      ) : p.status === "first_approved" ? (
                        <Badge className="rounded-sm gap-1 bg-blue-600 hover:bg-blue-600"><SealCheck size={12} /> Awaiting 2nd</Badge>
                      ) : (
                        <Badge variant="outline" className="rounded-sm gap-1 text-amber-600 border-amber-600/40"><Clock size={12} /> Created</Badge>
                      )}
                      {p.status === "paid" && <div className="text-[10px] text-muted-foreground mt-0.5">{p.paid_date}{p.reference ? ` · ${p.reference}` : ""}</div>}
                      {p.status === "approved" && p.approved_by && <div className="text-[10px] text-muted-foreground mt-0.5">by {p.approved_by}</div>}
                      {p.status === "first_approved" && p.first_approver && <div className="text-[10px] text-muted-foreground mt-0.5">1st by {p.first_approver}</div>}
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="text-[10px] text-primary hover:underline inline-flex items-center gap-1 mt-1" data-testid={`trail-${p.key}`}><ClockCounterClockwise size={11} /> Trail</button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 bg-card">
                          <div className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2">Approval & Payment Trail</div>
                          <div className="space-y-2">
                            {p.trail.map((t, i) => (
                              <div key={i} className="flex gap-2 text-sm">
                                <div className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                                <div>
                                  <div className="font-medium">{t.action}</div>
                                  <div className="text-xs text-muted-foreground">{t.by}{t.at ? ` · ${new Date(t.at).toLocaleString()}` : ""}</div>
                                  {t.note && <div className="text-xs text-muted-foreground font-mono">{t.note}</div>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                    <TableCell className="text-right">
                      {p.status === "paid" ? (
                        <Button variant="ghost" size="sm" onClick={() => unpay(p)} data-testid={`unpay-${p.key}`} className="h-7 rounded-sm gap-1 text-xs text-muted-foreground"><ArrowCounterClockwise size={14} /> Revert</Button>
                      ) : p.status === "approved" ? (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" onClick={() => openPay(p)} data-testid={`pay-${p.key}`} className="h-7 rounded-sm gap-1 text-xs bg-emerald-700 hover:bg-emerald-800"><Money size={14} /> Mark Paid</Button>
                          <Button variant="ghost" size="sm" onClick={() => reset(p)} data-testid={`reset-${p.key}`} className="h-7 rounded-sm gap-1 text-xs text-muted-foreground"><ArrowCounterClockwise size={14} /></Button>
                        </div>
                      ) : p.status === "first_approved" ? (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" onClick={() => approve(p)} data-testid={`approve-${p.key}`} className="h-7 rounded-sm gap-1 text-xs bg-blue-600 hover:bg-blue-700"><SealCheck size={14} /> 2nd Approve</Button>
                          <Button variant="ghost" size="sm" onClick={() => reset(p)} data-testid={`reset-${p.key}`} className="h-7 rounded-sm gap-1 text-xs text-muted-foreground"><ArrowCounterClockwise size={14} /></Button>
                        </div>
                      ) : (
                        <Button size="sm" onClick={() => approve(p)} data-testid={`approve-${p.key}`} className="h-7 rounded-sm gap-1 text-xs"><CheckCircle size={14} /> {p.requires_two_step ? "1st Approve" : "Approve"}</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent className="max-w-md bg-card">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Mark Payment as Paid</DialogTitle>
            <DialogDescription>
              {payDialog && `${payDialog.lease_title} · Period ${payDialog.period} · ${money(payDialog.amount, payDialog.currency)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Actual payment date</Label>
              <Input type="date" value={payForm.paid_date} onChange={(e) => setPayForm((f) => ({ ...f, paid_date: e.target.value }))}
                className="mt-1.5 rounded-sm font-mono" data-testid="pay-date" />
            </div>
            <div>
              <Label>Payment reference</Label>
              <Input value={payForm.reference} onChange={(e) => setPayForm((f) => ({ ...f, reference: e.target.value }))}
                className="mt-1.5 rounded-sm" placeholder="Bank txn ID, cheque no., etc." data-testid="pay-reference" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitPay} className="rounded-sm gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid="confirm-pay-btn"><Money size={16} /> Confirm Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
